import React, { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../../components/form_fields/Label";
import { useUpdateEmployee } from "../../../../../services/employee.service";
import {
	EMPLOYEE_PAY_INFO_TYPE,
	EmployeeDto,
} from "../../../../../models/employee.dto";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import {
	useCreateEmployeeAccountInfo,
	useDeleteEmployeeAccountInfo,
} from "../../../../../services/employee-account-info.service";
import { useManageTableWithSearchParamsData } from "../../../../../helpers/useManageTable";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { PaymentInstitutionBranchDto } from "../../../../../models/payment-institution-branch.dto";
import { EmployeeFormContext } from "../../EmployeeFormContext";
import API from "../../../../../services/utils/api";
import { useAsyncEffect, useLockFn } from "ahooks";
import { toast } from "react-toastify";
import useEditFormStore from "../../../../../state-management/useEditFormStore";

interface IFormInputs {
	splitPayment: boolean;
	primaryPayPercent: number;
	primaryPaymentChannel: EMPLOYEE_PAY_INFO_TYPE;
	primaryInstitutionId: number;
	primaryInstitutionBranchId?: number;
	primaryAccountNumber: string;
	secondaryPaymentChannel?: EMPLOYEE_PAY_INFO_TYPE;
	secondaryInstitutionId?: number;
	secondaryInstitutionBranchId?: number;
	secondaryAccountNumber?: string;
}

interface IPaymentInfo {
	formData: any;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	setView: (view: string) => void;
	closeForm: () => void;
	tableAPIRef: React.MutableRefObject<GridApiCommunity>;
	defaultPrimaryInstitutionBranches: PaymentInstitutionBranchDto[];
	defaultSecondaryInstitutionBranches: PaymentInstitutionBranchDto[];
	reloadTableData?: () => void;
}

const PaymentInfo = ({
	formData,
	setView,
	setFormData,
	closeForm,
	tableAPIRef,
	defaultPrimaryInstitutionBranches,
	defaultSecondaryInstitutionBranches,
	reloadTableData: reloadTableDataProp,
}: IPaymentInfo) => {
	const { formDataValues } = useEditFormStore();
	const { paymentInstitutions } = useContext(EmployeeFormContext);
	const [uploading, setUploading] = useState(false);

	const [primaryInstitutionBranches, setPrimaryBankBranches] = useState<
		PaymentInstitutionBranchDto[]
	>(defaultPrimaryInstitutionBranches);
	const [secondaryInstitutionBranches, setSecondaryBankBranches] = useState<
		PaymentInstitutionBranchDto[]
	>(defaultSecondaryInstitutionBranches);

	const {
		register,
		watch,
		setValue,
		handleSubmit,
		formState: { errors },
		clearErrors,
	} = useForm<IFormInputs>({
		defaultValues: formData,
	});

	useEffect(() => {
		if (formData?.secondaryInstitutionId || formData?.secondaryAccountNumber)
			return;

		clearErrors("secondaryInstitutionId");
		clearErrors("secondaryAccountNumber");
	}, [formData?.secondaryInstitutionId, formData?.secondaryAccountNumber]);

	useEffect(() => {
		const { unsubscribe } = watch((values) => {
			setFormData((prev) => ({ ...prev, ...values }));
		});
		return () => unsubscribe();
	}, [watch]);

	const uploadCompleted = (updatedData: EmployeeDto) => {
		try {
			reloadTableDataProp?.();
		} catch (error) {
			console.error("Error updating table:", error);
		} finally {
			setUploading(false);
			setTimeout(() => {
				closeForm();
			}, 0);
		}
	};

	const handlePaymentInstitutionChange = async (id: string, primary = true) => {
		if (!id) {
			if (primary) {
				setPrimaryBankBranches([]);
				setValue("primaryPaymentChannel", "");
			} else {
				setSecondaryBankBranches([]);
				setValue("secondaryPaymentChannel", undefined);
			}

			return;
		}

		const paymentInstitution = paymentInstitutions.find(
			(paymentInstitution) => paymentInstitution.id === Number(id),
		);

		if (!paymentInstitution) return;
		if (paymentInstitution.paymentChannelType !== "BANK") {
			if (primary) {
				setValue(
					"primaryPaymentChannel",
					paymentInstitution.paymentChannelType,
				);
				setValue("primaryInstitutionBranchId", undefined);
			} else {
				setValue(
					"secondaryPaymentChannel",
					paymentInstitution.paymentChannelType,
				);
				setValue("secondaryInstitutionBranchId", undefined);
			}

			return;
		}

		try {
			const paymentInstitutionBranches =
				await API.BillingAndSubscriptionAPI.getPaymentInstitutionBranch({
					limit: APP_CONFIG.PAGE_LIMIT,
					active: true,
					orderBy: "name:asc" as any,
					paymentInstitutionId: paymentInstitution.id,
				});

			if (primary) {
				setPrimaryBankBranches(paymentInstitutionBranches.data);
				setValue(
					"primaryPaymentChannel",
					paymentInstitution.paymentChannelType,
				);
			} else {
				setSecondaryBankBranches(paymentInstitutionBranches.data);
				setValue(
					"secondaryPaymentChannel",
					paymentInstitution.paymentChannelType,
				);
			}
		} catch {}
	};

	useAsyncEffect(
		useLockFn(async () => {
			if (
				formData?.primaryInstitutionId &&
				formData?.primaryPaymentChannel === "BANK" &&
				!primaryInstitutionBranches.length
			) {
				await handlePaymentInstitutionChange(formData.primaryInstitutionId);
			}

			if (
				formData?.secondaryInstitutionId &&
				formData?.secondaryPaymentChannel === "BANK" &&
				!secondaryInstitutionBranches.length
			) {
				await handlePaymentInstitutionChange(
					formData.secondaryInstitutionId,
					false,
				);
			}
		}),
		[],
	);

	useEffect(() => {
		if (
			formData?.primaryInstitutionBranchId &&
			primaryInstitutionBranches.length
		) {
			setValue(
				"primaryInstitutionBranchId",
				formData.primaryInstitutionBranchId,
			);
		}

		if (
			formData?.secondaryInstitutionBranchId &&
			secondaryInstitutionBranches.length
		) {
			setValue(
				"secondaryInstitutionBranchId",
				formData.secondaryInstitutionBranchId,
			);
		}
	}, [primaryInstitutionBranches, secondaryInstitutionBranches]);

	const { mutate: updateEmployee } = useUpdateEmployee();
	const { mutate: createEmployeeAccountInfo } = useCreateEmployeeAccountInfo();
	const { mutate: deleteEmployeeAccountInfo } = useDeleteEmployeeAccountInfo();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		const employeePayInfo = {
			basicPay: formData.basicPay,
			payPeriod: formData.payPeriod,
			splitPayment: data.splitPayment === "yes" ? true : false,
			primaryPayPercent:
				data.splitPayment === "no" ? 1 : data.primaryPayPercent / 100,
			primaryPaymentChannel: data.primaryPaymentChannel,
			secondaryPaymentChannel: data.secondaryPaymentChannel
				? data.secondaryPaymentChannel
				: "BANK",
			currencyId: formData.currencyId,
		};

		const employeeAccountInfo: any[] = [];
		const primaryAccountConfig = {
			type: data.primaryPaymentChannel,
			institutionId: Number(data.primaryInstitutionId),
			institutionBranchId: data.primaryInstitutionBranchId || undefined,
			accountNumber: data.primaryAccountNumber,
			primary: true,
			secondary: false,
			active: true,
			currencyId: formData.currencyId,
		};
		const secondaryAccountConfig = {
			type: data.secondaryPaymentChannel,
			institutionId: Number(data.secondaryInstitutionId),
			institutionBranchId: data.secondaryInstitutionBranchId || undefined,
			accountNumber: data.secondaryAccountNumber,
			primary: false,
			secondary: true,
			active: true,
			currencyId: formData.currencyId,
		};

		if (formData?.employeeAccountInfo?.length > 0) {
			employeeAccountInfo.push({
				id: formData?.employeeAccountInfo[0]?.id,
				...primaryAccountConfig,
			});
		}

		if (
			formData?.employeeAccountInfo?.length > 1 &&
			data.secondaryAccountNumber
		) {
			employeeAccountInfo.push({
				id: formData?.employeeAccountInfo[1]?.id,
				...secondaryAccountConfig,
			});
		}

		// The server rejects an empty email, so clearing one asks for its removal
		const enteredEmail = formData.email?.trim() || undefined;
		const removeEmail = !enteredEmail && !!formDataValues.email;
		let cooperateEmail: string | undefined = undefined;
		if (enteredEmail !== formDataValues.email) {
			cooperateEmail = enteredEmail;
		}

		let privateEmail: string | undefined = undefined;
		if (formData.privateEmail !== formDataValues.privateEmail) {
			privateEmail = formData.privateEmail;
		}

		let hrUpdate: boolean | undefined = undefined;
		if (formData.hr === formDataValues.hr) {
			hrUpdate = undefined;
		} else {
			hrUpdate = formData.hr === "yes" ? true : false;
		}

		const removeDepartmentId =
			formData.departmentId === null && formDataValues.departmentId
				? formDataValues.departmentId
				: undefined;

		const removeTribeId =
			formData.tribeId === null && formDataValues.tribeId
				? formDataValues.tribeId
				: undefined;

		const removeDivisionId =
			formData.divisionId === null && formDataValues.divisionId
				? formDataValues.divisionId
				: undefined;

		const removeStationId =
			formData.stationId === null && formDataValues.stationId
				? formDataValues.stationId
				: undefined;

		const removeRegionId =
			formData.regionId === null && formDataValues.regionId
				? formDataValues.regionId
				: undefined;

		const payload = {
			notchId: formData.notchId || undefined,
			employeeNumber: formData.employeeNumber,
			title: formData.title,
			firstName: formData.firstName,
			lastName: formData.lastName,
			otherNames: formData.otherNames || undefined,
			gender: formData.gender,
			dateOfBirth: formData.dateOfBirth,
			photoUrl: formData.photoUrl || undefined,
			ssn: formData.ssn || undefined,
			taxIdentificationNumber: formData.taxIdentificationNumber || undefined,
			majorGradeLevelId: formData.majorGradeLevelId || undefined,
			minorGradeLevelId: formData.minorGradeLevelId || undefined,
			nationality: formData.nationality,
			regionId: formData.regionId || undefined,
			tribeId: formData.tribeId || undefined,
			email: cooperateEmail,
			removeEmail: removeEmail || undefined,
			privateEmail: privateEmail,
			msisdn: formData.msisdn,
			address: formData.address || undefined,
			alternateMsisdn: formData.alternateMsisdn,
			digitalAddress: formData.digitalAddress || undefined,
			jobTitleId: formData.jobTitleId || undefined,
			departmentId: formData.departmentId || undefined,
			divisionId: formData.divisionId || undefined,
			stationId: formData.stationId || undefined,
			costAreaId: formData.costAreaId || undefined,
			status: formData.status,
			employmentDate: formData.employmentDate,
			terminationDate: formData.terminationDate || undefined,
			reemployed: formData.reemployed === "yes" ? true : false,
			resident: formData.resident === "yes" ? true : false,
			unionMember: formData.unionMember === "yes" ? true : false,
			employeePayInfo,
			employeeAccountInfo,
			hr: hrUpdate,
			pensioner: formData.pensioner,
			idInfo: formData.idInfo ?? [],
			excludeFromPayrollRun: formData.excludeFromPayrollRun,
			removeIdInfoIds: formData.removeIdInfoIds ?? [],
			removeDepartmentId,
			removeTribeId,
			removeDivisionId,
			removeStationId,
			removeRegionId,
		};

		updateEmployee(
			{ payload, id: formData?.id },
			{
				onSuccess: (updatedEmployee) => {
					let pendingOperations = 0;
					let hasError = false;

					const checkCompletion = () => {
						if (pendingOperations === 0 && !uploading) {
							uploadCompleted(updatedEmployee.data);
						}
					};

					const createSecondaryAccountInfo = () => {
						pendingOperations++;
						createEmployeeAccountInfo(
							{
								payload: {
									employeeId: formData?.id,
									...secondaryAccountConfig,
								},
							},
							{
								onSuccess: () => {
									toast.success(
										"Secondary Payment Channel created successfully",
									);
								},
								onError: (error) => {
									console.error("Secondary account creation failed:", error);
									hasError = true;
								},
								onSettled: () => {
									pendingOperations--;
									checkCompletion();
								},
							},
						);
					};

					if (!employeeAccountInfo[0] && data.primaryPaymentChannel) {
						pendingOperations++;
						createEmployeeAccountInfo(
							{
								payload: { employeeId: formData?.id, ...primaryAccountConfig },
							},
							{
								onSuccess: () => {
									toast.success("Primary Payment Channel created successfully");

									if (!employeeAccountInfo[1] && data.secondaryPaymentChannel) {
										createSecondaryAccountInfo();
									}
								},
								onError: (error) => {
									console.error("Primary account creation failed:", error);
									hasError = true;
								},
								onSettled: () => {
									pendingOperations--;
									checkCompletion();
								},
							},
						);
						return;
					}

					if (!employeeAccountInfo[1] && data.secondaryPaymentChannel) {
						createSecondaryAccountInfo();
						return;
					}

					if (
						formData?.employeeAccountInfo?.length > 1 &&
						!data.secondaryAccountNumber
					) {
						pendingOperations++;
						deleteEmployeeAccountInfo(
							{
								id: formData?.employeeAccountInfo[1]?.id,
							},
							{
								onSettled: () => {
									pendingOperations--;
									checkCompletion();
								},
							},
						);
						return;
					}

					uploadCompleted(updatedEmployee.data);
				},
				onError: (error) => {
					console.error("Update employee failed:", error);
					toast.error("Failed to update employee");
					setUploading(false);
				},
			},
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="mt-6">
				<div className="flex space-x-12 text-base">
					<h3>Split Payment</h3>
					<div className="flex space-x-3 items-center">
						<input
							type="radio"
							value={"yes"}
							{...register("splitPayment", {
								required: "Select an option",
							})}
							id="yes"
						/>
						<label htmlFor="yes">Yes</label>
					</div>
					<div className="flex space-x-3 items-center">
						<input
							type="radio"
							value={"no"}
							{...register("splitPayment", {
								required: "Select an option",
							})}
							id="no"
						/>
						<label htmlFor="no">No</label>
					</div>
				</div>
				{errors.splitPayment && (
					<p className="text-red-500 text-sm mt-1">
						{errors.splitPayment.message}
					</p>
				)}
			</div>

			{formData?.splitPayment !== "no" && (
				<div className="mt-6 flex flex-col">
					<Label title="Primary Payment Percentage" for="primaryPayPercent" />
					<div className="flex items-center mt-2">
						<input
							placeholder={"0 - 100"}
							type="number"
							{...register("primaryPayPercent", {
								required: "Enter primary payment percentage here",
								valueAsNumber: true,
								min: {
									value: 1,
									message: "Enter a value between 1 and 100",
								},
								max: {
									value: 100,
									message: "Enter a value between 1 and 100",
								},
							})}
							id="primaryPayPercent"
							step="any"
							className={`w-[38%] bg-secondary-gray rounded-tl-lg rounded-bl-lg h-14 px-5 text-sm  ${
								errors.primaryPayPercent
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						<div className="h-14 w-16 bg-black rounded-tr-lg rounded-br-lg text-white flex items-center justify-center">
							%
						</div>
					</div>
					{errors.primaryPayPercent && (
						<p className="text-red-500 text-sm mt-1">
							{errors.primaryPayPercent.message}
						</p>
					)}
				</div>
			)}

			<div className="mt-10">
				<h3 className="font-bold">Primary Payment Channel</h3>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6 mt-4">
					<div className="w-full">
						<Label title="Bank" for="primaryInstitutionId" />
						<select
							defaultValue={formData?.primaryInstitutionId}
							id="primaryInstitutionId"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.primaryInstitutionId
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("primaryInstitutionId", {
								required: "Select primary bank here",
								valueAsNumber: true,
							})}
							onInput={(e: any) =>
								handlePaymentInstitutionChange(e.target.value)
							}
						>
							<option value="">Select Bank</option>
							{paymentInstitutions?.map((paymentInstitution) => (
								<option
									key={paymentInstitution?.id}
									value={paymentInstitution?.id}
								>
									{paymentInstitution?.name}
								</option>
							))}
						</select>
						{errors.primaryInstitutionId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.primaryInstitutionId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Account Number" for="primaryAccountNumber" />
						<input
							defaultValue={formData?.primaryAccountNumber}
							placeholder="Enter number"
							type="number"
							{...register("primaryAccountNumber", {
								required: "Enter primary bank account number here",
							})}
							id="primaryAccountNumber"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.primaryAccountNumber
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.primaryAccountNumber && (
							<p className="text-red-500 text-sm mt-1">
								{errors.primaryAccountNumber.message}
							</p>
						)}
					</div>
					{formData?.primaryPaymentChannel === "BANK" && (
						<div className="w-full">
							<Label title="Bank Branch" for="primaryInstitutionBranchId" />
							<select
								defaultValue={formData?.primaryInstitutionBranchId || undefined}
								id="primaryInstitutionBranchId"
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.primaryInstitutionBranchId
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
								{...register("primaryInstitutionBranchId", {
									required: "Enter primary bank branch here",
								})}
							>
								<option value="">Select Branch</option>
								{primaryInstitutionBranches.map((paymentInstitutionBranch) => (
									<option
										key={paymentInstitutionBranch?.id}
										value={paymentInstitutionBranch?.id}
									>
										{paymentInstitutionBranch?.name}
									</option>
								))}
							</select>
							{errors.primaryInstitutionBranchId && (
								<p className="text-red-500 text-sm mt-1">
									{errors.primaryInstitutionBranchId.message}
								</p>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="mt-10">
				<h3 className="font-bold">
					Secondary Payment Channel <i>(optional)</i>
				</h3>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6 mt-4">
					<div className="w-full">
						<Label title="Bank" for="secondaryInstitutionId" />
						<select
							defaultValue={formData?.secondaryInstitutionId}
							id="secondaryInstitutionId"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								errors.secondaryInstitutionId
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("secondaryInstitutionId", {
								validate: (value, formData) => {
									if (!value && formData.secondaryAccountNumber) {
										return "Select bank or clear account number field";
									}

									return true;
								},
							})}
							onInput={(e: any) =>
								handlePaymentInstitutionChange(e.target.value, false)
							}
						>
							<option value="">Select Bank</option>
							{paymentInstitutions?.map((paymentInstitution) => (
								<option
									key={paymentInstitution?.id}
									value={paymentInstitution?.id}
								>
									{paymentInstitution?.name}
								</option>
							))}
						</select>
						{errors.secondaryInstitutionId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.secondaryInstitutionId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Account Number" for="secondaryAccountNumber" />
						<input
							defaultValue={formData?.secondaryAccountNumber}
							placeholder="Enter number"
							type="number"
							id="secondaryAccountNumber"
							{...register("secondaryAccountNumber", {
								validate: (value, formData) => {
									if (!value && formData.secondaryInstitutionId) {
										return "Enter account number or clear bank field";
									}

									return true;
								},
							})}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.secondaryAccountNumber
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.secondaryAccountNumber && (
							<p className="text-red-500 text-sm mt-1">
								{errors.secondaryAccountNumber.message}
							</p>
						)}
					</div>
					{formData?.secondaryPaymentChannel === "BANK" && (
						<div className="w-full">
							<Label title="Bank Branch" for="secondaryInstitutionBranchId" />
							<select
								defaultValue={formData?.secondaryInstitutionBranchId}
								id="secondaryInstitutionBranchId"
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.secondaryInstitutionBranchId
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
								{...register("secondaryInstitutionBranchId", {
									required: "Enter secondary bank branch or clear bank field",
									valueAsNumber: true,
								})}
							>
								<option value="">Select Branch</option>
								{secondaryInstitutionBranches.map(
									(paymentInstitutionBranch) => (
										<option
											key={paymentInstitutionBranch?.id}
											value={paymentInstitutionBranch?.id}
										>
											{paymentInstitutionBranch?.name}
										</option>
									),
								)}
							</select>
							{errors.secondaryInstitutionBranchId && (
								<p className="text-red-500 text-sm mt-1">
									{errors.secondaryInstitutionBranchId.message}
								</p>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="w-full flex items-center justify-end gap-4 mt-10">
				<button
					type="button"
					onClick={() => setView("employmentDetails")}
					className="h-14 px-8 border border-gray-300 rounded-lg text-sm text-gray-600"
				>
					Go Back
				</button>
				<button
					type="submit"
					disabled={uploading}
					className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
				>
					{uploading ? "Uploading..." : "Save Record"}
				</button>
			</div>
		</form>
	);
};

export default PaymentInfo;
